import React, { useState, useEffect } from "react";
import { Alert, Dropdown, Spinner } from "react-bootstrap";
import { emojis } from "../../bootstrap/alertData";
import DeleteModal from "../../DeleteModal";
import {
  getServiceCities,
  updateServiceCities,
  deleteServiceCity,
  createServiceCity,
} from "../../../../APIS";

const City = () => {
  const [cityData, setCityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cityToDelete, setCityToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const itemsPerPage = 10;

  // ✅ Fetch cities
  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await getServiceCities();
      setCityData(res?.data?.cities || []);
    } catch (err) {
      setError("Failed to fetch cities.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  // ✅ Toggle active status
  const handleToggleStatus = async (city) => {
    try {
      const updatedCity = { ...city, active: !city.active };
      await updateServiceCities(updatedCity, city.id);
      setSuccessMsg(
        `City "${city.name}" marked as ${updatedCity.active ? "Active" : "Inactive"
        }.`
      );
      fetchCities();
    } catch (err) {
      setError("Failed to update city status.");
    } finally {
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Add new city
  const handleAdd = () => {
    setFormData({ name: "", heading: "", description: "", active: true });
    setIsEditMode(true);
  };

  // ✅ Edit existing city
  const handleEdit = (city) => {
    setFormData(city);
    setIsEditMode(true);
  };

  // ✅ Delete city - show confirmation modal
  const handleDelete = (city) => {
    setCityToDelete(city);
    setShowDeleteModal(true);
  };

  // ✅ Confirm delete city
  const handleConfirmDelete = async () => {
    if (!cityToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteServiceCity(cityToDelete.id);
      setSuccessMsg("City deleted successfully.");
      fetchCities();
      setShowDeleteModal(false);
      setCityToDelete(null);
    } catch (err) {
      setError("Failed to delete city.");
    } finally {
      setDeleteLoading(false);
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await updateServiceCities(formData, formData.id);
        setSuccessMsg("City updated successfully.");
      } else {
        await createServiceCity(formData);
        setSuccessMsg("City added successfully.");
      }
      setIsEditMode(false);
      fetchCities();
    } catch (err) {
      setError("Failed to save city.");
    } finally {
      setTimeout(() => {
        setError(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  // ✅ Filter + Pagination
  const filteredCities = cityData.filter((city) =>
    city.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredCities.length / itemsPerPage);
  const paginatedCities = filteredCities.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="col-12">
      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>
          {emojis.success} {successMsg}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {emojis.warning} {error}
        </Alert>
      )}

      {isEditMode && formData ? (
        // ✅ Add/Edit Form
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {formData.id ? "Edit City" : "Add City"}
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      City Name<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter city name..."
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Status</label>
                    <div className="col-lg-6">
                      <select
                        name="active"
                        value={formData.active}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            active: e.target.value === "true",
                          }))
                        }
                        className="form-control"
                      >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-xl-12">
                  <div className="mb-3 row">
                    <label className="col-lg-2 col-form-label">
                      Heading
                    </label>
                    <div className="col-lg-10">
                      <input
                        type="text"
                        name="heading"
                        value={formData.heading || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter heading..."
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-12">
                  <div className="mb-3 row">
                    <label className="col-lg-2 col-form-label">
                      Description
                    </label>
                    <div className="col-lg-10">
                      <textarea
                        name="description"
                        value={formData.description || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter description..."
                        rows="4"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary ms-2"
                      onClick={() => setIsEditMode(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        // ✅ Listing Cities
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">Cities</h4>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by city name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <button
                className="btn btn-secondary"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleAdd}
              >
                Add City
              </button>
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center p-4">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>City Name</th>
                      <th>Heading</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCities.length > 0 ? (
                      [...paginatedCities]
                      .sort((a,b) => a.id - b.id)
                      .map((city,i)=>(
                        <tr key={city.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{city.name || "--"}</td>
                          <td>{city.heading || "--"}</td>
                          <td>
                            {city.description 
                              ? (city.description.length > 50 
                                  ? city.description.substring(0, 50) + "..." 
                                  : city.description)
                              : "--"}
                          </td>
                          <td>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={city.active}
                                onChange={() => handleToggleStatus(city)}
                              />
                            </div>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="link">
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => handleEdit(city)}>
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDelete(city)}
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No cities found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {/* ✅ Pagination */}
            {!loading && filteredCities.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {currentPage * itemsPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * itemsPerPage, filteredCities.length)}{" "}
                  of {filteredCities.length} entries
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`btn btn-sm border me-1 ${currentPage === i ? "btn-primary text-white" : ""
                        }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary ms-2"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCityToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete City"
        message={`Are you sure you want to delete the city "${cityToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteLoading}
        danger={true}
      />
    </div>
  );
};

export default City;
